import type { CloudpackConfig, PackageDefinitionsCache, TargetEnvironment } from '@ms-cloudpack/common-types';
import { flattenExportsMap, getExportsMap } from '@ms-cloudpack/package-utilities';
import { normalizeRelativePath } from '@ms-cloudpack/path-string-parsing';
import { intermediateToSourcePath, isExternalPackage } from '@ms-cloudpack/path-utilities';
import path from 'path';
import { isValidBundleEntry } from './isValidBundleEntry.js';
import { toNormalizedEntryKey } from './toNormalizedEntryKey.js';

/**
 * Gets the entries map for a given package. An entries map is different from an exports map in that
 * the keys are relative paths to where the output should live, rather than how the entry is imported.
 * The data structure is used for bundler config; not for resolution of imports.
 */
export async function getEntriesMapFromPackage(
  options: {
    inputPath: string;
    targetEnvironment: TargetEnvironment;
  },
  context: {
    packages: PackageDefinitionsCache;
    config: Pick<CloudpackConfig, 'features'>;
  },
): Promise<Record<string, string>> {
  const { inputPath, targetEnvironment } = options;
  const isInternal = !isExternalPackage(inputPath);
  const exportsMap = await getExportsMap({ packagePath: inputPath }, context);
  const flattenedMap = flattenExportsMap(exportsMap, {
    conditions: `${targetEnvironment}-defaults`,
  });
  let sourceExportsMap: Record<string, string>;

  // The flattened map values tell us what physical files will show up in the import map on the page. We use these
  // as the keys for the entries map, which results in physical file output from the bundler.

  // For internal packages, we want the values (the inputs to the each entry) to use source paths if available. To
  // get these, we can either derive from exports map "source" entries, or if those are not available, we can attempt
  // to derive source paths from intermediate paths.

  if (isInternal) {
    sourceExportsMap = flattenExportsMap(exportsMap, {
      // Allow traversing into targetEnvironment conditions to find 'source' conditions
      conditions: [targetEnvironment, 'source'],
      requiredConditions: ['source'],
    });

    for (const [key, value] of Object.entries(flattenedMap)) {
      if (!sourceExportsMap[key]) {
        sourceExportsMap[key] = intermediateToSourcePath(value, inputPath) || value;
      }
    }
  }

  return Object.fromEntries(
    Object.entries(flattenedMap)
      .filter(([, value]) => isValidBundleEntry(value))
      .map(([key, value]) => [
        toNormalizedEntryKey(value),
        toNormalizedRelativePath(inputPath, sourceExportsMap?.[key] || value),
      ]),
  );
}

function toNormalizedRelativePath(inputPath: string, entry: string): string {
  if (path.isAbsolute(entry)) {
    entry = path.relative(inputPath, entry);
  }

  return normalizeRelativePath(entry);
}
